import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import {
  FOR_INVESTORS_SECTIONS,
} from 'constants/businessToolSections/forInvestors';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { FundingDirectionsContainer } from 'components/BusinessToolForm/Sections/ExecutiveSummary/FinancialNeedsExecSummary/components/FundingDirectionsContainer';
import { GLOBAL_FIELD_NAMES, globalPrefix } from 'components/BusinessToolForm/Sections/Global/form';
import {
  prefix,
  RAISING_AND_ALLOCATING_FUNDS_NAMES,
} from 'components/BusinessToolForm/Sections/RaisingAndAllocatingFunds/utils/form';

import { concatWithDot } from 'helpers/string';

export const RaisingAndAllocationFunds: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_INVESTORS_SECTIONS.RAISING_AND_ALLOCATING_FUNDS
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('raisingFunds.title')}
          section={FOR_INVESTORS_SECTIONS.RAISING_AND_ALLOCATING_FUNDS}
        />
      </FormSectionCard>
      {/* 9.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('raisingFunds.investmentTotalCost')}
        aiFieldName={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.INVESTMENT_TOTAL_COST)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.INVESTMENT_TOTAL_COST)}
          name={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.INVESTMENT_TOTAL_COST)}
          minRows={3}
          disabled={!canAccess}
        />
      </FormSectionCard>
      {/* 9.2 */}
      <FormSectionCard disabled={!canAccess} title={t('raisingFunds.findingDirections')}>
        <Grid container spacing={3}>
          <Grid item sm={6}>
            <NumberInputControlled
              control={control}
              id="executiveSummary.financialNeeds.totalCostOfYourProject"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.TOTAL_COST)}
              label={t('executiveSummary.financialNeeds.totalCostOfYourProject')}
              adornmentType="currency"
              disabled={!canAccess}
            />
          </Grid>
          <Grid item sm={12}>
            <FundingDirectionsContainer disabled={!canAccess} />
          </Grid>
        </Grid>
      </FormSectionCard>
      {/* 9.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('raisingFunds.fundingDirectionsDetails')}
        aiFieldName={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.FUNDING_DIRECTION_DETAILS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.FUNDING_DIRECTION_DETAILS)}
          name={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.FUNDING_DIRECTION_DETAILS)}
          minRows={3}
          disabled={!canAccess}
        />
      </FormSectionCard>
      {/* 9.4 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('raisingFunds.expectedFundingPeriod')}
        aiFieldName={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.EXPECTED_FUNDING_PERIOD)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.EXPECTED_FUNDING_PERIOD)}
          name={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.EXPECTED_FUNDING_PERIOD)}
          minRows={3}
          disabled={!canAccess}
        />
      </FormSectionCard>
      {/* 9.5 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('raisingFunds.financedPortionCost')}
        aiFieldName={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.FINANCED_PORTION_DESCRIPTION)}
      >
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <NumberInputControlled
              control={control}
              id={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.FINANCED_PORTION_COST)}
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PERCENTAGE_OF_FUNDING)}
              adornmentType="percentage"
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInputControl
              multiline
              control={control}
              id={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.FINANCED_PORTION_DESCRIPTION)}
              name={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.FINANCED_PORTION_DESCRIPTION)}
              minRows={3}
              disabled={!canAccess}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('raisingFunds.fundingCost')}
        aiFieldName={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.FUNDING_DESCRIPTION)}
      >
        <Grid container spacing={2}>
          {/* 9.6.1 */}
          <Grid item xs={4}>
            <NumberInputControlled
              control={control}
              id={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.FUNDING_AMOUNT)}
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.FUNDING_AMOUNT)}
              adornmentType="currency"
              disabled={!canAccess}
            />
            {/* 9.6.2 */}
          </Grid>
          <Grid item xs={12}>
            <TextInputControl
              multiline
              control={control}
              id={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.FUNDING_DESCRIPTION)}
              name={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.FUNDING_DESCRIPTION)}
              minRows={3}
              disabled={!canAccess}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('raisingFunds.raisingCost')}
        aiFieldName={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.RAISING_DESCRIPTION)}
      >
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <NumberInputControlled
              control={control}
              id={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.INVESTMENT_AMOUNT)}
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.INVESTMENT_AMOUNT)}
              adornmentType="currency"
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={12}>
            {/* 9.9 */}
            <TextInputControl
              multiline
              control={control}
              id={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.RAISING_DESCRIPTION)}
              name={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.RAISING_DESCRIPTION)}
              minRows={3}
              disabled={!canAccess}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      {/* 9.10 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('raisingFunds.terms')}
        aiFieldName={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.TERMS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.TERMS)}
          name={concatWithDot(prefix, RAISING_AND_ALLOCATING_FUNDS_NAMES.TERMS)}
          minRows={3}
          disabled={!canAccess}
        />
      </FormSectionCard>
    </>
  );
};
